import { VerificationPhase, VerificationRequestEvent } from 'matrix-js-sdk/src/crypto-api';
import { useCallback, useEffect, useState } from 'react';

import { isLoginSecretStorageUnlockActive } from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useAutoConfirmSas from 'contexts/MatrixDeviceVerificationContext/sasVerification/hooks/useAutoConfirmSas';
import useSasAutoStart from 'contexts/MatrixDeviceVerificationContext/sasVerification/hooks/useSasAutoStart';
import useSasDecimalNumbers from 'contexts/MatrixDeviceVerificationContext/sasVerification/hooks/useSasDecimalNumbers';
import useSasVerificationModals from 'contexts/MatrixDeviceVerificationContext/sasVerification/hooks/useSasVerificationModals';
import useSasVerificationState from 'contexts/MatrixDeviceVerificationContext/sasVerification/hooks/useSasVerificationState';
import { VerificationErrorEnum } from 'contexts/MatrixDeviceVerificationContext/utils';
import logger from 'utils/logging/faro';

import useSasCallbacks from './sasVerification/useSasCallbacks';

const useMatrixDeviceVerificationController = () => {
  const {
    crossSigningStatus,
    activeRequest,
    hasCompletedSelfVerification,
    requestVerification,
    acceptRequest,
    cancelRequest,
  } = useMatrixDeviceContext();
  const { matrixClient: contextClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { sasCallbacks } = useSasCallbacks(activeRequest);
  const [hasChosenSasVerification, setHasChosenSasVerification] = useState(false);
  useAutoConfirmSas({ sasCallbacks, activeRequest, crossSigningStatus });
  const {
    sasDecimalNumbers,
    isRequesting,
    isConfirming,
    errorKey,
    waitingModalFlowStarted,
    setSasDecimalNumbers,
    setIsRequesting,
    setIsConfirming,
    setErrorKey,
    setWaitingModalFlowStarted,
    resetControllerState,
  } = useSasVerificationState(activeRequest);
  useSasDecimalNumbers({ sasCallbacks, sasDecimalNumbers, setSasDecimalNumbers, setErrorKey });
  useSasAutoStart({ crossSigningStatus, activeRequest, setErrorKey, hasChosenSasVerification });
  const { shouldShowWaitingModal, shouldShowIncomingModal, isRequestReady, openIncomingModal, dismissWaitingModal } =
    useSasVerificationModals({
      crossSigningStatus,
      hasCompletedSelfVerification,
      activeRequest,
      hasChosenSasVerification,
    });

  const beginWaitingDeviceFlow = useCallback(async () => {
    const hasActiveNonTerminalRequest =
      activeRequest && ![VerificationPhase.Cancelled, VerificationPhase.Done].includes(activeRequest.phase);

    if (
      crossSigningStatus === CrossSigningStatusEnum.VERIFIED ||
      hasCompletedSelfVerification ||
      waitingModalFlowStarted ||
      isRequesting ||
      hasActiveNonTerminalRequest
    ) {
      return;
    }

    setWaitingModalFlowStarted(true);
    setErrorKey(null);
    setIsRequesting(true);

    try {
      const request = await requestVerification();
      if (!request) {
        setWaitingModalFlowStarted(false);
        setErrorKey(VerificationErrorEnum.FAILED_RETRY);
      }
    } catch (err) {
      setWaitingModalFlowStarted(false);
      logger.error('Failed to start verification', err);
      setErrorKey(VerificationErrorEnum.FAILED_RETRY);
    } finally {
      setIsRequesting(false);
    }
  }, [
    activeRequest,
    hasCompletedSelfVerification,
    crossSigningStatus,
    requestVerification,
    waitingModalFlowStarted,
    isRequesting,
    setWaitingModalFlowStarted,
    setErrorKey,
    setIsRequesting,
  ]);

  const acceptIncomingRequest = useCallback(async () => {
    setErrorKey(null);
    try {
      if (!activeRequest) return setErrorKey(VerificationErrorEnum.FAILED_RETRY);
      if (
        [VerificationPhase.Started, VerificationPhase.Cancelled, VerificationPhase.Done].includes(activeRequest.phase)
      ) {
        return;
      }

      await acceptRequest();

      if (![VerificationPhase.Ready, VerificationPhase.Started].includes(activeRequest.phase)) {
        await new Promise<void>((resolve, reject) => {
          const handler = () => {
            if ([VerificationPhase.Ready, VerificationPhase.Started].includes(activeRequest.phase)) {
              activeRequest.off(VerificationRequestEvent.Change, handler);
              resolve();
            } else if ([VerificationPhase.Cancelled, VerificationPhase.Done].includes(activeRequest.phase)) {
              activeRequest.off(VerificationRequestEvent.Change, handler);
              reject();
            }
          };
          activeRequest.on(VerificationRequestEvent.Change, handler);

          if ([VerificationPhase.Ready, VerificationPhase.Started].includes(activeRequest.phase)) {
            activeRequest.off(VerificationRequestEvent.Change, handler);
            resolve();
          }
        });
      }

      await activeRequest.startVerification('m.sas.v1');
    } catch (err) {
      logger.error('Failed to accept device verification request', err);
      setErrorKey(VerificationErrorEnum.FAILED_ACCEPT);
    }
  }, [acceptRequest, activeRequest, setErrorKey]);

  const confirmNumbersMatch = useCallback(async () => {
    if (!sasCallbacks) return setErrorKey(VerificationErrorEnum.WAITING_FOR_SAS_DATA);

    setIsConfirming(true);
    setErrorKey(null);
    try {
      sasCallbacks.confirm();

      if (activeRequest && !activeRequest.initiatedByMe) {
        const client = contextClient ?? (isLoginSecretStorageUnlockActive() ? matrixClientRef.current : null);
        const crypto = client?.getCrypto();
        const otherDeviceId = activeRequest.otherDeviceId;

        if (crypto && otherDeviceId) await crypto.crossSignDevice(otherDeviceId);
      }

      setHasChosenSasVerification(false);
    } catch (err) {
      logger.error('Failed to confirm verification', err);
      setErrorKey(VerificationErrorEnum.FAILED_RETRY);
    } finally {
      setIsConfirming(false);
    }
  }, [activeRequest, contextClient, matrixClientRef, sasCallbacks, setErrorKey, setIsConfirming]);

  const cancelVerification = useCallback(
    (shouldDismissWaitingModal = true, shouldResetSasChoice = true) => {
      if (shouldResetSasChoice) setHasChosenSasVerification(false);
      cancelRequest();
      resetControllerState();
      setWaitingModalFlowStarted(false);
      if (shouldDismissWaitingModal) dismissWaitingModal();
    },
    [cancelRequest, resetControllerState, dismissWaitingModal, setWaitingModalFlowStarted]
  );

  useEffect(() => {
    if (activeRequest && activeRequest.phase === VerificationPhase.Done) setHasChosenSasVerification(false);
  }, [activeRequest]);

  // Another device resetting keys invalidates this one — clear SAS-in-progress state so the picker can show.
  useEffect(() => {
    if (crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED) {
      setHasChosenSasVerification(false);
      setWaitingModalFlowStarted(false);
    }
  }, [crossSigningStatus, setWaitingModalFlowStarted]);

  return {
    sasDecimalNumbers,
    isRequesting,
    isConfirming,
    errorKey,
    shouldShowWaitingModal,
    shouldShowIncomingModal,
    beginWaitingDeviceFlow,
    acceptIncomingRequest,
    confirmNumbersMatch,
    cancelVerification,
    isRequestReady,
    openIncomingModal,
    hasChosenSasVerification,
    setHasChosenSasVerification,
    setIsRequesting,
    setSasDecimalNumbers,
  };
};

export default useMatrixDeviceVerificationController;
